import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, User } from "lucide-react";
import { findIndustry, industries } from "@/data/industries";

export default function IndustryDetail() {
  const { slug } = useParams();
  const industry = findIndustry(slug);

  if (!industry) {
    return <Navigate to="/industries" replace />;
  }

  const others = industries.filter((i) => i.slug !== slug);

  return (
    <main data-testid={`page-industry-${slug}`} className="pt-24">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end pt-16 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={industry.image}
            alt={industry.name}
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/85 to-[#050505]" />
          <div className="absolute inset-0 grid-bg opacity-30" />
        </div>
        <div className="container-x relative">
          <Link
            to="/industries"
            data-testid="industry-back-link"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#00F0FF] mb-8 transition-colors"
          >
            <ArrowLeft size={14} /> All industries
          </Link>
          <div className="font-mono-label text-xs text-[#00F0FF] mb-4">
            Industry practice
          </div>
          <h1
            data-testid="industry-heading"
            className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight max-w-4xl leading-[1.02]"
          >
            {industry.name}
          </h1>
          <p className="mt-6 text-[#00F0FF] text-lg">{industry.tagline}</p>
          <p className="mt-6 text-white/70 max-w-3xl leading-relaxed text-lg">
            {industry.summary}
          </p>

          <div className="mt-12 grid grid-cols-3 gap-4 md:gap-8 max-w-2xl">
            {industry.metrics.map((m, i) => (
              <div
                key={i}
                data-testid={`industry-metric-${i}`}
                className="border-l border-[#00F0FF]/40 pl-4"
              >
                <div className="font-display text-3xl md:text-4xl text-white">
                  {m.value}
                </div>
                <div className="font-mono-label text-[10px] text-white/50 mt-1">
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-24 md:py-32 border-t border-white/10">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="font-mono-label text-xs text-[#00F0FF] mb-4">
                / Use cases
              </div>
              <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight max-w-2xl">
                Where we&apos;ve done the work.
              </h2>
            </div>
            <p className="text-white/60 max-w-md leading-relaxed">
              Not a catalogue — a sample of the problems we actively engineer
              solutions for today.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {industry.useCases.map((uc, i) => (
              <div
                key={uc.title}
                data-testid={`usecase-${i}`}
                className="group rounded-2xl border border-white/10 bg-[#0F0F13] p-8 transition-transform duration-300 hover:-translate-y-1 hover:border-[#00F0FF]/40"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg grid place-items-center bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] shrink-0">
                    <CheckCircle2 size={18} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <div className="font-mono-label text-[10px] text-white/40 mb-2">
                      Use case 0{i + 1}
                    </div>
                    <h3 className="font-display text-xl md:text-2xl text-white tracking-tight">
                      {uc.title}
                    </h3>
                    <p className="mt-3 text-white/60 leading-relaxed">
                      {uc.body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User stories */}
      <section className="py-24 md:py-32 border-t border-white/10 relative">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <div className="container-x relative">
          <div className="mb-14">
            <div className="font-mono-label text-xs text-[#00F0FF] mb-4">
              / User stories
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight max-w-3xl">
              The people we{" "}
              <span className="text-[#00FF66]">build for.</span>
            </h2>
            <p className="mt-6 text-white/60 max-w-2xl leading-relaxed">
              Every engagement starts with a real person, a real problem, and
              a job to be done. Here are three from this practice.
            </p>
          </div>

          <div className="space-y-6">
            {industry.userStories.map((s, i) => (
              <article
                key={i}
                data-testid={`user-story-${i}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 rounded-2xl border border-white/10 bg-[#0F0F13] p-8 md:p-10"
              >
                <div className="md:col-span-3 flex md:flex-col items-start gap-3">
                  <div className="w-12 h-12 rounded-full grid place-items-center bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF]">
                    <User size={20} />
                  </div>
                  <div>
                    <div className="font-mono-label text-[10px] text-white/40">
                      Persona
                    </div>
                    <div className="font-display text-lg text-white leading-tight mt-1">
                      {s.persona}
                    </div>
                  </div>
                </div>
                <div className="md:col-span-9">
                  <div className="font-mono-label text-[10px] text-[#00F0FF] mb-3">
                    Story {String(i + 1).padStart(2, "0")}
                  </div>
                  <p className="font-display text-xl md:text-2xl text-white leading-snug tracking-tight">
                    &ldquo;{s.story}&rdquo;
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Other industries */}
      <section className="py-24 border-t border-white/10">
        <div className="container-x">
          <div className="font-mono-label text-xs text-[#00F0FF] mb-6">
            / Other practices
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {others.map((ind) => (
              <Link
                key={ind.slug}
                to={`/industries/${ind.slug}`}
                data-testid={`other-industry-${ind.slug}`}
                className="group flex items-center justify-between p-6 rounded-xl border border-white/10 bg-[#0F0F13] hover:border-[#00F0FF]/40 transition-colors"
              >
                <div>
                  <div className="font-display text-lg text-white">
                    {ind.name}
                  </div>
                  <div className="text-sm text-white/50 mt-1">
                    {ind.tagline}
                  </div>
                </div>
                <ArrowRight
                  size={18}
                  className="text-white/40 group-hover:text-[#00F0FF] group-hover:translate-x-1 transition-all"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
